import React, { useState, useEffect } from "react";
import { sellerService } from "../../services/enhancedCrudService";
import { toast } from "react-toastify";

const SanitarywareInquiryClickList = () => {
  const [inquiryClicks, setInquiryClicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchInquiryClicks = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        page_size: pagination.pageSize,
      };
      
      const response = await sellerService.getSanitarywareInquiryClickList(params);
      
      if (response && response.inquiry_clicks) {
        setInquiryClicks(response.inquiry_clicks);
        setPagination((prev) => ({
          ...prev,
          total: response.count || 0,
          totalPages: Math.ceil((response.count || 0) / pagination.pageSize),
        }));
      } else {
        setInquiryClicks([]);
      }
    } catch (error) {
      console.error("Error fetching sanitaryware inquiry clicks:", error);
      toast.error("Failed to load inquiry click list");
      setInquiryClicks([]);
    } finally {
      setLoading(false);
    }
  };


  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };


  useEffect(() => {
    fetchInquiryClicks();
  }, [pagination.page, pagination.pageSize]);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h4 className="card-title">Sanitaryware Inquiry Click List</h4>
            <div className="card-actions">
              <span className="badge badge-info">
                Total: {pagination.total} clicks
              </span>
            </div>
          </div>
          <div className="card-body">
            {/* Sanitaryware Inquiry Clicks Table */}
            <div className="table-responsive">
              {loading ? (
                <div className="text-center py-3">
                  <div
                    className="spinner-border spinner-border-sm text-primary"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2 text-muted">Loading...</span>
                </div>
              ) : (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Sr.No</th>
                      <th>Manufacturing Company Name</th>
                      <th>Product Details</th>
                      <th>Grade</th>
                      <th>Buyer Information</th>
                      <th>Contact</th>
                      <th>Address</th>
                      <th>Last Clicked</th>
                      <th>Click Count</th>
                    </tr>
                </thead>
                <tbody>
                  {inquiryClicks.length === 0 ? (
                    <tr>
                        <td colSpan="11" className="text-center py-4">
                          <div className="text-muted">
                            <i className="fas fa-search fa-2x mb-2"></i>
                            <p>No inquiry clicks found</p>
                          </div>
                        </td>
                    </tr>
                  ) : (
                    inquiryClicks.map((click, index) => (
                      <tr 
                        key={click.buyer?.id || index}
                        style={{
                          backgroundColor: index % 2 === 0 ? '#ffffffff' : '#d4d4d4ff',
                          color: '#495057'
                        }}
                      >
                        <td style={{padding: '12px', border: 'none'}}>
                          {(pagination.page - 1) * pagination.pageSize +
                            index +
                            1}
                        </td>
                        <td>
                          {click.product?.manufacturing_company_name || "N/A"}
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <span className="me-2">{click.product?.name || click.product?.product_name}</span>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleViewProduct(click.product)}
                              title="View Product Details"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          </div>
                        </td>
                        <td>{click.product?.grade || "N/A"}</td>
                        <td>
                          <div>
                            <strong>
                              {click.buyer?.owner_name || "N/A"}
                            </strong>
                          </div>
                        </td>
                        <td>
                          <strong className="small">
                            {click.buyer?.office_mobile || "N/A"}
                          </strong>
                        </td>
                        <td>
                          <div>
                            <strong className="small">
                              {click.buyer?.address?.address_line || "N/A"}
                            </strong>
                            <div className="small">
                              {click.buyer?.address?.city},{" "}
                              {click.buyer?.address?.state_name_display}
                            </div>
                          </div>
                        </td>
                        <td>
                          <strong className="small">
                            {formatDate(click.last_clicked_at)}
                          </strong>
                        </td>
                        <td>
                          <span className="badge badge-info">
                            {click.click_count} click
                            {click.click_count !== 1 ? "s" : ""}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              )}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-3">
                <nav>
                  <ul className="pagination justify-content-center">
                    <li
                      className={`page-item ${
                        pagination.page === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {Array.from(
                      { length: pagination.totalPages },
                      (_, i) => i + 1
                    ).map((page) => (
                      <li
                        key={page}
                        className={`page-item ${
                          page === pagination.page ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    ))}

                    <li
                      className={`page-item ${
                        pagination.page === pagination.totalPages
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
                <div className="text-center mt-2">
                  <small className="text-muted">
                    Showing {(pagination.page - 1) * pagination.pageSize + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.pageSize,
                      pagination.total
                    )}{" "}
                    of {pagination.total} entries
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Details Modal */}
      {showModal && selectedProduct && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Sanitaryware Product Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">Basic Information</h6>
                    <table className="table table-bordered">
                      <tbody>
                        <tr>
                          <td><strong>Product Name:</strong></td>
                          <td>{selectedProduct.name}</td>
                        </tr>
                        <tr>
                          <td><strong>Description:</strong></td>
                          <td>{selectedProduct.description || "N/A"}</td>
                        </tr>
                        <tr>
                          <td><strong>Manufacturing Company:</strong></td>
                          <td>{selectedProduct.manufacturing_company_name}</td>
                        </tr>
                        <tr>
                          <td><strong>Loading Address:</strong></td>
                          <td>{selectedProduct.loading_address || "N/A"}</td>
                        </tr>
                        <tr>
                          <td><strong>Seller Place:</strong></td>
                          <td>{selectedProduct.seller_place || "N/A"}</td>
                        </tr>
                        <tr>
                          <td><strong>Category:</strong></td>
                          <td>{selectedProduct.category || "N/A"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">Product Specifications</h6>
                    <table className="table table-bordered">
                      <tbody>
                        <tr>
                          <td width="40%"><strong>Grade:</strong></td>
                          <td width="60%">
                            <span className="badge badge-success">
                              {selectedProduct.grade || "N/A"}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Color:</strong></td>
                          <td>{selectedProduct.color || "N/A"}</td>
                        </tr>
                        <tr>
                          <td><strong>Glaze:</strong></td>
                          <td>{selectedProduct.glaze || "N/A"}</td>
                        </tr>
                        <tr>
                          <td><strong>Size:</strong></td>
                          <td>{selectedProduct.size || "N/A"}</td>
                        </tr>
                        <tr>
                          <td><strong>Weight:</strong></td>
                          <td>{selectedProduct.weight_kg ? `${selectedProduct.weight_kg} kg` : "N/A"}</td>
                        </tr>
                        <tr>
                          <td><strong>Sanitaryware Type:</strong></td>
                          <td>{selectedProduct.sanitaryware_type || "N/A"}</td>
                        </tr>
                        <tr>
                          <td><strong>Logo:</strong></td>
                          <td>{selectedProduct.logo || "N/A"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">Quantity & Packing</h6>
                    <table className="table table-bordered">
                      <tbody>
                        <tr>
                          <td width="40%"><strong>Quantity:</strong></td>
                          <td width="60%">{selectedProduct.quantity || "N/A"}</td>
                        </tr>
                        <tr>
                          <td><strong>Quantity Range:</strong></td>
                          <td>{selectedProduct.quantity_range || "N/A"}</td>
                        </tr>
                        <tr>
                          <td><strong>Packing:</strong></td>
                          <td>{selectedProduct.packing || "N/A"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">Status & Media</h6>
                    <table className="table table-bordered">
                      <tbody>
                        <tr>
                          <td width="40%"><strong>Status:</strong></td>
                          <td width="60%">
                            <span className={`badge ${selectedProduct.is_active ? 'badge-success' : 'badge-danger'}`}>
                              {selectedProduct.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Published:</strong></td>
                          <td>
                            <span className={`badge ${selectedProduct.is_published ? 'badge-success' : 'badge-warning'}`}>
                              {selectedProduct.is_published ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Image Count:</strong></td>
                          <td>{selectedProduct.img_count || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>PDF Count:</strong></td>
                          <td>{selectedProduct.pdf_count || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Uploaded At:</strong></td>
                          <td>{formatDate(selectedProduct.uploaded_at)}</td>
                        </tr>
                        <tr>
                          <td><strong>Modified At:</strong></td>
                          <td>{formatDate(selectedProduct.modified_at)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                {selectedProduct.hidden_district_name && selectedProduct.hidden_district_name.length > 0 && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <h6 className="text-primary mb-3">Hidden Districts</h6>
                      <div className="d-flex flex-wrap">
                        {selectedProduct.hidden_district_name.map((district, index) => (
                          <span key={index} className="badge badge-warning me-2 mb-2">
                            {district}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {selectedProduct.features && selectedProduct.features.length > 0 && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <h6 className="text-primary mb-3">Features</h6>
                      <div className="d-flex flex-wrap">
                        {selectedProduct.features.map((feature, index) => (
                          <span key={index} className="badge badge-light me-2 mb-2">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showModal && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
};

export default SanitarywareInquiryClickList;
